import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/adminApi";

const GROUPS = [
  {
    name: "Navigation",
    fields: [
      ["nav_about", "About link"],
      ["nav_services", "Services link"],
      ["nav_work", "Work link"],
      ["nav_contact", "Contact link"],
    ],
  },
  {
    name: "Hero",
    fields: [
      ["hero_eyebrow", "Eyebrow line"],
      ["hero_headline_1", "Headline — line 1"],
      ["hero_headline_2a", "Headline — line 2 (plain part)"],
      ["hero_headline_2b", "Headline — line 2 (accent part)"],
      ["hero_subheading", "Subheading", true],
      ["hero_cta_work", "Button — work"],
      ["hero_cta_contact", "Button — contact"],
    ],
  },
  {
    name: "Sections",
    fields: [
      ["practice_label", "Practice section label"],
      ["work_label", "Work section label"],
      ["capabilities_label", "Capabilities label"],
      ["capabilities_intro", "Capabilities intro", true],
      ["strip1_label", "Strip 1 — label"], ["strip1_text", "Strip 1 — text", true], ["strip1_cta", "Strip 1 — button"],
      ["strip2_label", "Strip 2 — label"], ["strip2_text", "Strip 2 — text", true], ["strip2_cta", "Strip 2 — button"],
      ["strip3_label", "Strip 3 — label"], ["strip3_text", "Strip 3 — text", true], ["strip3_cta", "Strip 3 — button"],
    ],
  },
  {
    name: "Contact",
    fields: [
      ["contact_label", "Label"],
      ["contact_headline_a", "Headline (plain part)"],
      ["contact_headline_b", "Headline (accent part)"],
      ["contact_paragraph", "Paragraph", true],
      ["contact_submit", "Submit button"],
    ],
  },
  {
    name: "Footer",
    fields: [["footer_email", "Email address"]],
  },
];

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/50 transition-colors";

export const SiteTextTab = () => {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/admin/site-text")
      .then(({ data }) => setValues(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/site-text", { values });
      setValues(data);
      toast.success("Site text saved — the public site now shows your wording.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!values) {
    return <div className="py-16 text-center text-white/40 text-sm">Loading site text…</div>;
  }

  return (
    <div>
      {GROUPS.map((g) => (
        <div key={g.name} className="mb-8">
          <h3 className="text-[11px] uppercase tracking-[0.3em] text-[#c8b99d] mb-4">{g.name}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map(([key, label, long]) => (
              <div key={key} className={long ? "md:col-span-2" : ""}>
                <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">{label}</span>
                {long ? (
                  <textarea
                    rows={2}
                    data-testid={`site-text-${key}`}
                    className={`${inputCls} resize-none`}
                    value={values[key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                ) : (
                  <input
                    data-testid={`site-text-${key}`}
                    className={inputCls}
                    value={values[key] || ""}
                    onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={save}
        disabled={saving}
        data-testid="site-text-save"
        className="inline-flex items-center gap-2 bg-white text-black px-7 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors disabled:opacity-50"
      >
        {saving && <Loader2 size={14} className="animate-spin" />}
        {saving ? "Saving…" : "Save All Text"}
      </button>
    </div>
  );
};

export default SiteTextTab;
